import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Popper, Typography, ButtonBase, Paper, ClickAwayListener } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import { Link, useLocation } from 'react-router-dom';
import useImage from 'services/Hooks/useImage';
import { getNavLinkBehavior } from 'services/Utilities/navLinkUtils';

const TOUCH_INPUT_QUERY = '(hover: none), (pointer: coarse)';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  navItem: {
    height: ({ rowHeight }) => rowHeight,
    padding: '0 16px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.05rem',
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    color: ({ color }) => color,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  link: {
    color: 'inherit',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    height: '100%',
  },
  popoverPaper: {
    position: 'relative',
    backgroundColor: ({ contentBackground }) => contentBackground,
    color: ({ contentColor }) => contentColor,
    boxShadow: theme.shadows[6],
    borderRadius: 4,
    marginTop: 4,
    width: 'fit-content',
    minWidth: 320,
    maxWidth: 'calc(100vw - 24px)',
    overflow: 'visible',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -8,
      left: 64,
      transform: 'translateX(-50%)',
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: `8px solid ${theme.palette.primary.dark}`,
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -10,
      left: 64,
      transform: 'translateX(-50%)',
      borderLeft: '10px solid transparent',
      borderRight: '10px solid transparent',
      borderBottom: '10px solid #000',
      zIndex: 0,
      opacity: 1,
    },
  },
  popoverInner: {
    display: 'flex',
    alignItems: 'stretch',
    maxWidth: 'calc(100vw - 24px)',
    borderRadius: 4,
    overflow: 'hidden',
  },
  popoverContent: {
    padding: 16,
    width: 'auto',
    minWidth: 320,
    maxWidth: 'min(42vw, 500px)',
    flex: '0 1 420px',
  },
  sectionTitle: {
    color: theme.palette.secondary.main,
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: '0.03rem',
    marginBottom: 6,
  },
  sectionGroup: {
    paddingTop: 10,
  },
  menuLink: {
    display: 'block',
    padding: '6px 0',
    color: 'inherit',
    textDecoration: 'none',
    letterSpacing: '0.03rem',
    fontFamily: theme.typography.fontFamily,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  divider: {
    height: 1,
    margin: '8px 0',
    backgroundColor: alpha(theme.palette.common.white, 0.08),
  },
  previewPaper: {
    backgroundColor: alpha(theme.palette.common.white, 0.03),
    borderLeft: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
    padding: 0,
    display: 'block',
    position: 'relative',
    alignSelf: 'stretch',
    minWidth: 560,
    minHeight: 260,
    maxWidth: 'min(75vw, 1200px)',
    flex: '1 1 920px',
    overflow: 'hidden',
  },
  previewImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'block',
    borderRadius: 0,
    objectFit: 'cover',
    border: 'none',
  },
  menuRowContent: {
    display: 'inline-flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    gap: 8,
  },
  menuCaret: {
    opacity: 0.85,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
  },
  imageOnlyPreviewPaper: {
    borderLeft: 'none',
    width: 'min(70vw, 700px)',
    minWidth: 360,
    minHeight: 300,
    maxHeight: 'min(70vh, 520px)',
    flex: '0 0 auto',
  },
}));

const SiteNav = ({ items, align, background, color, contentBackground, contentColor, rowHeight, breakpoint, style }) => {
  const matches = useMediaQuery(`(min-width:${breakpoint}px)`);
  const isTouchInput = useMediaQuery(TOUCH_INPUT_QUERY);
  const location = useLocation();
  const classes = useStyles({ color, contentBackground, contentColor, rowHeight });
  const { getDealerPortalMenuImage } = useImage();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [submenuPreviewSrc, setSubmenuPreviewSrc] = useState(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const popoverRef = useRef(null);
  const rootRef = useRef(null);
  const OPEN_DELAY = 50;
  const CLOSE_DELAY = 250;
  const getPreviewFileId = menuNode => menuNode?.menuAttachedFiles?.[0]?.id || null;
  const hasSubmenuPreview = menuNode => Boolean(menuNode?.id && getPreviewFileId(menuNode));
  const isTouchInteractionMode = matches && isTouchInput;

  const sortedItems = useMemo(() => (items || []).slice().sort((a, b) => a.order - b.order), [items]);

  const activeItemHasChildren = (activeItem?.children || []).length > 0;
  const activeItemFallbackPreviewSrc =
    activeItem && !activeItemHasChildren && hasSubmenuPreview(activeItem)
      ? getDealerPortalMenuImage(activeItem.id, getPreviewFileId(activeItem))
      : null;
  const activePreviewSrc = submenuPreviewSrc || activeItemFallbackPreviewSrc;

  const closeMenu = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }

    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }

    setActiveItem(null);
    setAnchorEl(null);
    setSubmenuPreviewSrc(null);
  };

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const handleOpen = (item, event) => {
    const target = event.currentTarget;
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }

    const openMenu = () => {
      if (!target) {
        return;
      }

      const isImageOnlyMenu = !(item?.children?.length > 0) && hasSubmenuPreview(item);
      setActiveItem(item);
      setAnchorEl(target);
      setSubmenuPreviewSrc(isImageOnlyMenu ? getDealerPortalMenuImage(item.id, getPreviewFileId(item)) : null);
    };

    if (activeItem) {
      openMenu();
      return;
    }

    openTimer.current = setTimeout(openMenu, OPEN_DELAY);
  };

  const scheduleClose = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }
    closeTimer.current = setTimeout(() => {
      closeMenu();
    }, CLOSE_DELAY);
  };

  const handlePopoverEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handlePopoverLeave = () => {
    scheduleClose();
  };

  const clearSubmenuPreview = () => {
    setSubmenuPreviewSrc(null);
  };

  const handleClickAway = event => {
    if (!activeItem) {
      return;
    }

    if (rootRef.current?.contains(event.target)) {
      return;
    }

    closeMenu();
  };

  const handleTopClick = (item, event, itemLinkBehavior, itemHasSubmenuImage) => {
    const hasExpandableContent = item.type !== 'item' || itemHasSubmenuImage;

    if (!hasExpandableContent) {
      scheduleClose();
      return;
    }

    if (!isTouchInteractionMode) {
      if (itemLinkBehavior.link) {
        scheduleClose();
      }
      return;
    }

    const isActive = activeItem?.name === item.name;

    if (!isActive || !itemLinkBehavior.link) {
      event.preventDefault();
      event.stopPropagation();
      handleOpen(item, event);
      return;
    }

    scheduleClose();
  };

  const renderMenuTextWithCaret = menuNode => (
    <Box className={classes.menuRowContent}>
      <span>{menuNode?.name}</span>
      {hasSubmenuPreview(menuNode) ? <ChevronRight fontSize="small" className={classes.menuCaret} /> : null}
    </Box>
  );

  const showSubmenuPreview = menuNode => {
    const menuId = menuNode?.id;
    const attachedFileId = getPreviewFileId(menuNode);
    if (!menuId || !attachedFileId) {
      setSubmenuPreviewSrc(null);
      return;
    }

    setSubmenuPreviewSrc(getDealerPortalMenuImage(menuId, attachedFileId));
  };

  if (!matches) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        ref={rootRef}
        className={classes.root}
        sx={{
          justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
          backgroundColor: background,
        }}
        style={style}
        onMouseEnter={isTouchInteractionMode ? undefined : handlePopoverEnter}
        onMouseLeave={isTouchInteractionMode ? undefined : scheduleClose}>
        {sortedItems.map(item => {
          const itemHasSubmenuImage = hasSubmenuPreview(item);
          const itemLinkBehavior = getNavLinkBehavior(item);

          if (item.type === 'item' && !itemHasSubmenuImage) {
            return (
              <ButtonBase
                key={item.name}
                className={classes.navItem}
                component={itemLinkBehavior.isExternalRoute ? 'a' : Link}
                to={itemLinkBehavior.isExternalRoute ? undefined : itemLinkBehavior.link?.toLocaleLowerCase()}
                href={itemLinkBehavior.isExternalRoute ? itemLinkBehavior.link : undefined}
                target={itemLinkBehavior.isExternalRoute ? itemLinkBehavior.target : undefined}
                rel={itemLinkBehavior.isExternalRoute ? itemLinkBehavior.rel : undefined}
                data-tour={item.dataTour || undefined}
                onMouseEnter={isTouchInteractionMode ? undefined : scheduleClose}
                onClick={scheduleClose}>
                <Typography variant="h5">{item.name}</Typography>
              </ButtonBase>
            );
          }

          return (
            <Box key={item.name}>
              <ButtonBase
                className={classes.navItem}
                component={itemLinkBehavior.link ? (itemLinkBehavior.isExternalRoute ? 'a' : Link) : 'button'}
                to={itemLinkBehavior.link && !itemLinkBehavior.isExternalRoute ? itemLinkBehavior.link : undefined}
                href={itemLinkBehavior.link && itemLinkBehavior.isExternalRoute ? itemLinkBehavior.link : undefined}
                target={itemLinkBehavior.link && itemLinkBehavior.isExternalRoute ? itemLinkBehavior.target : undefined}
                rel={itemLinkBehavior.link && itemLinkBehavior.isExternalRoute ? itemLinkBehavior.rel : undefined}
                onMouseEnter={isTouchInteractionMode ? undefined : event => handleOpen(item, event)}
                onClick={event => handleTopClick(item, event, itemLinkBehavior, itemHasSubmenuImage)}
                data-tour={item.dataTour || undefined}
                aria-haspopup="true"
                aria-expanded={activeItem?.name === item.name ? 'true' : undefined}>
                <Typography variant="h5">{item.name}</Typography>
              </ButtonBase>
            </Box>
          );
        })}

        <Popper
          open={Boolean(activeItem)}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 6],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 12,
                altAxis: true,
                tether: true,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
              },
            },
          ]}
          style={{ zIndex: 1300 }}>
          <Paper
            className={classes.popoverPaper}
            ref={popoverRef}
            onMouseEnter={isTouchInteractionMode ? undefined : handlePopoverEnter}
            onMouseLeave={isTouchInteractionMode ? undefined : handlePopoverLeave}
            elevation={6}>
            <Box className={classes.popoverInner}>
              {activeItemHasChildren && (
                <Box className={classes.popoverContent}>
                  {activeItem.children?.map(child => {
                    const hasChildren = child.type === 'collapse' && (child.children || []).length > 0;
                    const childLinkBehavior = getNavLinkBehavior(child);
                    if (!hasChildren) {
                      return (
                        <Box
                          key={child.name}
                          component={childLinkBehavior.isExternalRoute ? 'a' : Link}
                          to={childLinkBehavior.isExternalRoute ? undefined : childLinkBehavior.link}
                          href={childLinkBehavior.isExternalRoute ? childLinkBehavior.link : undefined}
                          target={childLinkBehavior.isExternalRoute ? childLinkBehavior.target : undefined}
                          rel={childLinkBehavior.isExternalRoute ? childLinkBehavior.rel : undefined}
                          className={classes.menuLink}
                          onClick={scheduleClose}
                          onMouseEnter={() => showSubmenuPreview(child)}>
                          <Typography variant="h5">{renderMenuTextWithCaret(child)}</Typography>
                        </Box>
                      );
                    }

                    return (
                      <Box key={child.name} className={classes.sectionGroup}>
                        <Typography
                          className={classes.sectionTitle}
                          variant="h4"
                          component={childLinkBehavior.link ? (childLinkBehavior.isExternalRoute ? 'a' : Link) : 'span'}
                          to={
                            childLinkBehavior.link && !childLinkBehavior.isExternalRoute ? childLinkBehavior.link : undefined
                          }
                          href={
                            childLinkBehavior.link && childLinkBehavior.isExternalRoute ? childLinkBehavior.link : undefined
                          }
                          target={
                            childLinkBehavior.link && childLinkBehavior.isExternalRoute
                              ? childLinkBehavior.target
                              : undefined
                          }
                          rel={
                            childLinkBehavior.link && childLinkBehavior.isExternalRoute ? childLinkBehavior.rel : undefined
                          }
                          onMouseEnter={() => showSubmenuPreview(child)}
                          onClick={childLinkBehavior.link ? scheduleClose : undefined}>
                          {renderMenuTextWithCaret(child)}
                        </Typography>
                        {(child.children || [])
                          .slice()
                          .sort((a, b) => a.order - b.order)
                          .map(grandchild => {
                            const hasNestedChildren =
                              grandchild.type === 'collapse' && (grandchild.children || []).length > 0;
                            const grandchildLinkBehavior = getNavLinkBehavior(grandchild);

                            if (!hasNestedChildren) {
                              return (
                                <Box
                                  key={grandchild.name}
                                  component={grandchildLinkBehavior.isExternalRoute ? 'a' : Link}
                                  to={grandchildLinkBehavior.isExternalRoute ? undefined : grandchildLinkBehavior.link}
                                  href={grandchildLinkBehavior.isExternalRoute ? grandchildLinkBehavior.link : undefined}
                                  target={grandchildLinkBehavior.isExternalRoute ? grandchildLinkBehavior.target : undefined}
                                  rel={grandchildLinkBehavior.isExternalRoute ? grandchildLinkBehavior.rel : undefined}
                                  className={classes.menuLink}
                                  onClick={scheduleClose}
                                  onMouseEnter={() => showSubmenuPreview(grandchild)}
                                  style={{ paddingLeft: '16px' }}>
                                  <Typography variant="h5">{renderMenuTextWithCaret(grandchild)}</Typography>
                                </Box>
                              );
                            }

                            return (
                              <Box key={grandchild.name} sx={{ mt: 1 }}>
                                <Typography
                                  className={classes.sectionTitle}
                                  variant="h5"
                                  component={
                                    grandchildLinkBehavior.link
                                      ? grandchildLinkBehavior.isExternalRoute
                                        ? 'a'
                                        : Link
                                      : 'span'
                                  }
                                  to={
                                    grandchildLinkBehavior.link && !grandchildLinkBehavior.isExternalRoute
                                      ? grandchildLinkBehavior.link
                                      : undefined
                                  }
                                  href={
                                    grandchildLinkBehavior.link && grandchildLinkBehavior.isExternalRoute
                                      ? grandchildLinkBehavior.link
                                      : undefined
                                  }
                                  target={
                                    grandchildLinkBehavior.link && grandchildLinkBehavior.isExternalRoute
                                      ? grandchildLinkBehavior.target
                                      : undefined
                                  }
                                  rel={
                                    grandchildLinkBehavior.link && grandchildLinkBehavior.isExternalRoute
                                      ? grandchildLinkBehavior.rel
                                      : undefined
                                  }
                                  onMouseEnter={() => showSubmenuPreview(grandchild)}
                                  onClick={grandchildLinkBehavior.link ? scheduleClose : undefined}
                                  sx={{ fontSize: '0.9em', paddingLeft: '16px' }}>
                                  {renderMenuTextWithCaret(grandchild)}
                                </Typography>
                                {(grandchild.children || [])
                                  .slice()
                                  .sort((a, b) => a.order - b.order)
                                  .map(item => {
                                    const itemLinkBehavior = getNavLinkBehavior(item);

                                    return (
                                      <Box
                                        key={item.name}
                                        component={itemLinkBehavior.isExternalRoute ? 'a' : Link}
                                        to={itemLinkBehavior.isExternalRoute ? undefined : itemLinkBehavior.link}
                                        href={itemLinkBehavior.isExternalRoute ? itemLinkBehavior.link : undefined}
                                        target={itemLinkBehavior.isExternalRoute ? itemLinkBehavior.target : undefined}
                                        rel={itemLinkBehavior.isExternalRoute ? itemLinkBehavior.rel : undefined}
                                        className={classes.menuLink}
                                        onClick={scheduleClose}
                                        onMouseEnter={() => showSubmenuPreview(item)}
                                        style={{ paddingLeft: '32px' }}>
                                        <Typography variant="h5">{renderMenuTextWithCaret(item)}</Typography>
                                      </Box>
                                    );
                                  })}
                              </Box>
                            );
                          })}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {activePreviewSrc ? (
                <Box
                  className={`${classes.previewPaper} ${
                    !activeItemHasChildren ? classes.imageOnlyPreviewPaper : ''
                  }`.trim()}>
                  <img src={activePreviewSrc} alt="submenu" className={classes.previewImage} onError={clearSubmenuPreview} />
                </Box>
              ) : null}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

SiteNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  background: PropTypes.string,
  color: PropTypes.string,
  contentBackground: PropTypes.string,
  contentColor: PropTypes.string,
  rowHeight: PropTypes.number,
  breakpoint: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  style: PropTypes.shape({}),
};

SiteNav.defaultProps = {
  align: 'center',
  background: '#000',
  color: '#fff',
  contentBackground: '#1f1f1f',
  contentColor: '#fff',
  rowHeight: 50,
  breakpoint: 768,
  style: undefined,
};

export default SiteNav;
